import { useCallback, useEffect, useId, useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate } from "react-router";
import { ApiError, api, type PairState, type PairSummary } from "../data/api.js";
import { CopyableUrl } from "./CopyableUrl.js";
import { device, type StoredInvite } from "../data/device.js";
import { useIdentity } from "../data/identity.js";

// Páry na Skice a v Moje já (REQ-034, REQ-035): karta „Jak se potkáte?“ s pozvánkou a seznamem
// párů, karta souhlasu po testu z pozvánky. Souhlas je jen kliknutí na tlačítko pod zněním
// `pair_link/1` (consent.pair_link.v1.*). Vypnuté páry (API 404) se neukážou vůbec.

/** Kdo se prokazuje: zařízení vlastnickým tokenem, nebo identita (cookie) se sezením výsledku. */
export type PairOwner = { readonly kind: "device"; readonly session: { readonly id: string; readonly token: string } } | { readonly kind: "identity"; readonly sessionId: string };

const ownerSession = (o: PairOwner) => (o.kind === "device" ? o.session : undefined);
const ownerSessionId = (o: PairOwner) => (o.kind === "device" ? o.session.id : o.sessionId);

/** Stavy, ve kterých má pár stránku (REQ-035). */
const OPENABLE: ReadonlySet<PairState> = new Set(["ready", "incomparable", "same_person", "unavailable"]);

/** Tlačítko s potvrzením: první klik ukáže znění a teprve druhý akci provede. */
export function Confirm({ label, text, yes, onConfirm, testId }: { label: ReactNode; text: ReactNode; yes: ReactNode; onConfirm: () => Promise<void>; testId: string }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!open) {
    return (
      <button type="button" className="link-button" onClick={() => setOpen(true)} data-testid={testId}>
        {label}
      </button>
    );
  }
  return (
    <div className="confirm stack" role="group">
      <p className="small">{text}</p>
      <div className="confirm__actions">
        <button
          type="button"
          className="button button--quiet"
          disabled={busy}
          data-testid={`${testId}-yes`}
          onClick={() => {
            setBusy(true);
            void onConfirm().finally(() => {
              setBusy(false);
              setOpen(false);
            });
          }}
        >
          {yes}
        </button>
        <button type="button" className="link-button" onClick={() => setOpen(false)}>
          <FormattedMessage id="pairs.confirmNo" />
        </button>
      </div>
    </div>
  );
}

/** Seznam párů se stavem a akcemi (Skica i Moje já). */
export function PairList({ pairs, owner, source, onChange }: { pairs: readonly PairSummary[]; owner: PairOwner | null; source: "result_page" | "me_page"; onChange: () => void }) {
  const { locale } = useIntl();
  const navigate = useNavigate();
  if (pairs.length === 0) return null;
  async function open(p: PairSummary) {
    if (owner !== null && owner.kind === "device") await device.setPairSession(p.id, owner.session.id);
    void navigate(`/${locale}/pairs/${p.id}`);
  }
  return (
    <ul className="pair-list stack" data-testid="pair-list">
      {pairs.map((p) => (
        <li key={p.id} className="pair-list__item" data-state={p.state} data-testid="pair-item">
          <p>
            <strong>{p.other_label}</strong> · <span className="muted small" data-testid="pair-state">
              <FormattedMessage id={`pairs.state.${p.state}`} />
            </span>
          </p>
          <div className="pair-list__actions">
            {OPENABLE.has(p.state) ? (
              <button type="button" className="button button--quiet" onClick={() => void open(p)} data-testid="pair-open">
                <FormattedMessage id="pairs.open" />
              </button>
            ) : null}
            {p.state === "waiting" && p.side === "a" ? (
              <Confirm
                testId="pair-cancel"
                label={<FormattedMessage id="pairs.cancelInvite" />}
                text={<FormattedMessage id="consent.pair_link.v1.cancel" />}
                yes={<FormattedMessage id="pairs.cancelInvite" />}
                onConfirm={() => api.revokePair(p.id, source, owner === null ? undefined : ownerSession(owner)).then(onChange)}
              />
            ) : null}
            {OPENABLE.has(p.state) ? (
              <Confirm
                testId="pair-revoke"
                label={<FormattedMessage id="pairs.revoke" />}
                text={<FormattedMessage id="consent.pair_link.v1.revoke" />}
                yes={<FormattedMessage id="pairs.revoke" />}
                onConfirm={() => api.revokePair(p.id, source, owner === null ? undefined : ownerSession(owner)).then(onChange)}
              />
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  );
}

function Created({ path, expires, emailSent, name, onDone }: { path: string; expires: string; emailSent: boolean; name: string; onDone: () => void }) {
  const intl = useIntl();
  const url = `${window.location.origin}${path}`;
  const date = intl.formatDate(expires, { day: "numeric", month: "numeric", year: "numeric" });
  return (
    <div className="stack" data-testid="pair-created">
      <h3>
        <FormattedMessage id="pairs.created.title" />
      </h3>
      <p className="small">
        <FormattedMessage id="pairs.created.lead" values={{ name, date }} />
      </p>
      <CopyableUrl url={url} testId="pair-link" copiedId="pairs.created.copied" />
      {typeof navigator.share === "function" ? (
        <button type="button" className="button button--quiet" onClick={() => void navigator.share({ url }).catch(() => undefined)}>
          <FormattedMessage id="pairs.created.share" />
        </button>
      ) : null}
      {emailSent ? (
        <p className="small" role="status">
          <FormattedMessage id="pairs.created.emailSent" />
        </p>
      ) : null}
      <p className="small muted">
        <FormattedMessage id="pairs.created.once" />
      </p>
      <button type="button" className="link-button" onClick={onDone}>
        <FormattedMessage id="pairs.created.done" />
      </button>
    </div>
  );
}

function InviteForm({ owner, onCreated, onBack }: { owner: PairOwner; onCreated: (c: { path: string; expires: string; emailSent: boolean; name: string }) => void; onBack: () => void }) {
  const { locale } = useIntl();
  const id = useId();
  const [inviter, setInviter] = useState("");
  const [invitee, setInvitee] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<"label" | "failed" | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.createPair(
        { session_id: ownerSessionId(owner), inviter_label: inviter, invitee_label: invitee, locale, ...(email.trim() === "" ? {} : { invitee_email: email.trim() }) },
        ownerSession(owner)?.token,
      );
      onCreated({ path: r.invite_path, expires: r.expires_at, emailSent: r.email_sent, name: invitee.trim() });
    } catch (e) {
      setError(e instanceof ApiError && (e.code === "invalid_label" || e.code === "invalid_request") ? "label" : "failed");
    } finally {
      setBusy(false);
    }
  }
  return (
    <form
      className="stack"
      data-testid="pair-form"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <label className="stack" htmlFor={`${id}-inviter`}>
        <span>
          <FormattedMessage id="pairs.form.inviter" />
        </span>
        <input id={`${id}-inviter`} required maxLength={40} value={inviter} onChange={(e) => setInviter(e.target.value)} data-testid="pair-inviter" />
        <span className="small muted">
          <FormattedMessage id="pairs.form.inviterHint" />
        </span>
      </label>
      <label className="stack" htmlFor={`${id}-invitee`}>
        <span>
          <FormattedMessage id="pairs.form.invitee" />
        </span>
        <input id={`${id}-invitee`} required maxLength={40} value={invitee} onChange={(e) => setInvitee(e.target.value)} data-testid="pair-invitee" />
        <span className="small muted">
          <FormattedMessage id="pairs.form.inviteeHint" />
        </span>
      </label>
      <label className="stack" htmlFor={`${id}-email`}>
        <span>
          <FormattedMessage id="pairs.form.email" />
        </span>
        <input id={`${id}-email`} type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="pair-email" />
        <span className="small muted">
          <FormattedMessage id="pairs.form.emailHint" />
        </span>
      </label>
      <p className="small consent-text" data-testid="pair-consent">
        <FormattedMessage id="consent.pair_link.v1.invite" />
      </p>
      {error === null ? null : (
        <p className="small" role="alert">
          <FormattedMessage id={error === "label" ? "pairs.form.invalidLabel" : "pairs.form.failed"} />
        </p>
      )}
      <div className="confirm__actions">
        <button type="submit" className="button" disabled={busy} data-testid="pair-submit">
          <FormattedMessage id="pairs.form.submit" />
        </button>
        <button type="button" className="link-button" onClick={onBack}>
          <FormattedMessage id="pairs.form.back" />
        </button>
      </div>
    </form>
  );
}

/** Karta „Jak se potkáte?“ na Skice majitele (REQ-034): pozvánka a páry tohoto výsledku. */
export function PairSection({ owner }: { owner: PairOwner }) {
  const [data, setData] = useState<{ can_invite: boolean; pairs: readonly PairSummary[] } | null>(null);
  const [mode, setMode] = useState<"idle" | "form" | { path: string; expires: string; emailSent: boolean; name: string }>("idle");
  const sessionId = ownerSessionId(owner);
  const token = ownerSession(owner)?.token;
  const load = useCallback(() => {
    api.sessionPairs(sessionId, token).then(setData, () => setData(null));
  }, [sessionId, token]);
  useEffect(load, [load]);
  if (data === null || (!data.can_invite && data.pairs.length === 0)) return null;
  return (
    <section className="card stack pair-card" data-testid="pair-section">
      <h2>
        <FormattedMessage id="pairs.card.title" />
      </h2>
      {mode === "idle" ? (
        <>
          <p className="small">
            <FormattedMessage id="pairs.card.lead" />
          </p>
          {data.can_invite ? (
            <button type="button" className="button" onClick={() => setMode("form")} data-testid="pair-invite">
              <FormattedMessage id="pairs.card.invite" />
            </button>
          ) : null}
        </>
      ) : mode === "form" ? (
        <InviteForm
          owner={owner}
          onBack={() => setMode("idle")}
          onCreated={(c) => {
            setMode(c);
            load();
          }}
        />
      ) : (
        <Created {...mode} onDone={() => setMode("idle")} />
      )}
      {data.pairs.length > 0 ? (
        <>
          <h3 className="h-small">
            <FormattedMessage id="pairs.list.title" />
          </h3>
          <PairList pairs={data.pairs} owner={owner} source="result_page" onChange={load} />
        </>
      ) : null}
    </section>
  );
}

/**
 * Karta souhlasu po testu z pozvánky (REQ-034): nahoře Skicy sezení, které zařízení založilo
 * z pozvánky. „Teď ne“ nic nezapíše, karta se ukáže příště znovu.
 */
export function JoinCard({ session }: { session: { readonly id: string; readonly token: string } }) {
  const { locale } = useIntl();
  const navigate = useNavigate();
  const identity = useIdentity();
  const [invite, setInvite] = useState<StoredInvite | null>(null);
  const [state, setState] = useState<"open" | "hidden" | "declined" | "gone" | "self" | "mismatch">("open");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    void device.sessionInvite(session.id).then((i) => setInvite(i ?? null));
  }, [session.id]);
  if (invite === null || state === "hidden") return null;

  async function finish() {
    await device.clearSessionInvite(session.id);
  }
  async function join() {
    if (invite === null) return;
    setBusy(true);
    try {
      const r = await api.inviteAccept(invite.token, "result_page", session);
      await device.setAcceptedPair(invite.token, r.pair_id);
      await device.setPairSession(r.pair_id, session.id);
      await finish();
      void navigate(`/${locale}/pairs/${r.pair_id}`);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : "";
      setState(code === "invite_gone" ? "gone" : code === "pair_self" ? "self" : code === "result_mismatch" ? "mismatch" : "open");
      if (code === "invite_gone") await finish();
    } finally {
      setBusy(false);
    }
  }
  if (state === "declined" || state === "gone" || state === "self" || state === "mismatch") {
    const id = state === "declined" ? "pairs.invite.declined" : state === "gone" ? "pairs.invite.gone.title" : state === "self" ? "pairs.invite.self" : "pairs.invite.mismatch";
    return (
      <section className="card stack pair-card" data-testid="join-card" data-state={state}>
        <p className="small" role="status">
          <FormattedMessage id={id} />
        </p>
      </section>
    );
  }
  return (
    <section className="card stack pair-card pair-card--join" data-testid="join-card">
      <h2>
        <FormattedMessage id="pairs.join.title" values={{ name: invite.inviter }} />
      </h2>
      <p className="small consent-text" data-testid="join-consent">
        <FormattedMessage id="consent.pair_link.v1.accept" />
      </p>
      {identity.kind === "anon" ? (
        <p className="small muted">
          <Link to={`/${locale}/me`}>
            <FormattedMessage id="pairs.join.login" />
          </Link>
        </p>
      ) : null}
      <div className="confirm__actions">
        <button type="button" className="button" disabled={busy} onClick={() => void join()} data-testid="join-yes">
          <FormattedMessage id="pairs.join.yes" />
        </button>
        <button type="button" className="link-button" onClick={() => setState("hidden")} data-testid="join-later">
          <FormattedMessage id="pairs.join.later" />
        </button>
      </div>
      <Confirm
        testId="join-decline"
        label={<FormattedMessage id="pairs.join.decline" />}
        text={<FormattedMessage id="consent.pair_link.v1.decline" />}
        yes={<FormattedMessage id="pairs.join.decline" />}
        onConfirm={async () => {
          await api.inviteDecline(invite.token, "result_page").catch((e: unknown) => {
            if (!(e instanceof ApiError && e.status === 410)) throw e;
          });
          await finish();
          setState("declined");
        }}
      />
    </section>
  );
}
